import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Clock, 
  Car, 
  Play,
  Square,
  Gauge,
  Fuel,
  MapPin,
  Calendar,
  Timer,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export default function ShiftPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeShift, setActiveShift] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  
  // Start shift form
  const [startForm, setStartForm] = useState({
    vehicle_id: '',
    km_start: '',
    notes: ''
  });
  
  // End shift form
  const [endForm, setEndForm] = useState({
    km_end: '',
    fuel_added: false,
    fuel_liters: '',
    fuel_cost: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [shiftRes, vehiclesRes] = await Promise.all([
        api().get('/shifts/active'),
        api().get('/vehicles/active')
      ]);
      setActiveShift(shiftRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startShift = async () => {
    if (!startForm.vehicle_id || !startForm.km_start) {
      toast.error('Selecione a viatura e indique os quilómetros iniciais');
      return;
    }

    try {
      const response = await api().post('/shifts/start', {
        vehicle_id: startForm.vehicle_id,
        km_start: parseFloat(startForm.km_start),
        notes: startForm.notes
      });
      
      toast.success('Turno iniciado com sucesso!');
      setShowStartModal(false);
      setStartForm({ vehicle_id: '', km_start: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar turno');
    }
  };

  const endShift = async () => {
    if (!endForm.km_end) {
      toast.error('Indique os quilómetros finais');
      return;
    }

    if (parseFloat(endForm.km_end) < activeShift.km_start) {
      toast.error('Quilómetros finais não podem ser menores que os iniciais');
      return;
    }

    try {
      const response = await api().post('/shifts/end', {
        km_end: parseFloat(endForm.km_end),
        fuel_added: endForm.fuel_added,
        fuel_liters: endForm.fuel_added ? parseFloat(endForm.fuel_liters) : null,
        fuel_cost: endForm.fuel_added && endForm.fuel_cost ? parseFloat(endForm.fuel_cost) : null,
        notes: endForm.notes
      });
      
      toast.success('Turno terminado com sucesso!');
      setShowEndModal(false);
      setEndForm({ km_end: '', fuel_added: false, fuel_liters: '', fuel_cost: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao terminar turno');
    }
  };

  const calculateDuration = (startTime) => {
    if (!startTime) return '-';
    const start = parseISO(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60); // minutes
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shift-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Clock className="h-6 w-6 text-amber-500" />
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Turno</h1>
          <p className="text-muted-foreground text-sm">Registo de início e fim de turno</p>
        </div>
      </div>

      {activeShift ? (
        /* Active Shift View */
        <Card className="border-2 border-emerald-500 bg-emerald-50/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-full animate-pulse">
                  <Play className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-emerald-800">Turno em Curso</CardTitle>
                  <CardDescription>
                    Iniciado às {format(parseISO(activeShift.start_time), "HH:mm 'de' d MMM", { locale: pt })}
                  </CardDescription>
                </div>
              </div>
              <Badge className="bg-emerald-500 text-white text-lg px-3 py-1">
                {calculateDuration(activeShift.start_time)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Vehicle Info */}
            <div className="p-4 bg-white rounded-lg border">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Car className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Viatura</p>
                  <p className="font-semibold text-lg">
                    {activeShift.vehicle_brand} {activeShift.vehicle_model}
                  </p>
                  <p className="font-mono text-amber-600 text-xl">{activeShift.vehicle_plate}</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Gauge className="h-4 w-4" />
                  <span className="text-sm">Km Inicial</span>
                </div>
                <p className="text-2xl font-bold tabular-nums">{activeShift.km_start.toLocaleString()} km</p>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Timer className="h-4 w-4" />
                  <span className="text-sm">Duração</span>
                </div>
                <p className="text-2xl font-bold">{calculateDuration(activeShift.start_time)}</p>
              </div>
            </div>

            {/* End Shift Button */}
            <Button 
              onClick={() => setShowEndModal(true)}
              className="w-full bg-red-500 hover:bg-red-600 text-white h-14 text-lg"
              data-testid="end-shift-btn"
            >
              <Square className="h-5 w-5 mr-2" />
              Terminar Turno
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* No Active Shift */
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Nenhum turno ativo</h3>
              <p className="text-muted-foreground mb-6">
                Inicie um novo turno para registar a sua atividade
              </p>
              <Button 
                onClick={() => setShowStartModal(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-8 text-lg"
                data-testid="start-shift-btn"
              >
                <Play className="h-5 w-5 mr-2" />
                Iniciar Turno
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            Informação
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• Ao iniciar o turno, selecione a viatura e registe os quilómetros no conta-quilómetros.</p>
          <p>• Ao terminar, registe os quilómetros finais e se abasteceu combustível.</p>
          <p>• Os dados ficam disponíveis nos relatórios diários e mensais.</p>
        </CardContent>
      </Card>

      {/* Start Shift Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="w-[95vw] max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Play className="h-5 w-5 text-emerald-500" />
              Iniciar Turno
            </DialogTitle>
            <DialogDescription>
              Selecione a viatura e registe os quilómetros iniciais
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Viatura *</Label>
              <Select
                value={startForm.vehicle_id}
                onValueChange={(value) => setStartForm({...startForm, vehicle_id: value})}
              >
                <SelectTrigger data-testid="vehicle-select">
                  <SelectValue placeholder="Selecione a viatura" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      <div className="flex items-center gap-2">
                        <Car className="h-4 w-4" />
                        <span>{v.brand} {v.model}</span>
                        <span className="font-mono text-amber-600">{v.plate}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="km_start">Quilómetros Iniciais *</Label>
              <div className="relative">
                <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="km_start"
                  type="number"
                  value={startForm.km_start}
                  onChange={(e) => setStartForm({...startForm, km_start: e.target.value})}
                  placeholder="Ex: 125000"
                  className="pl-10"
                  data-testid="km-start-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Verifique o conta-quilómetros da viatura</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea
                id="notes"
                value={startForm.notes}
                onChange={(e) => setStartForm({...startForm, notes: e.target.value})}
                placeholder="Observações..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowStartModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={startShift}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="confirm-start-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              Iniciar Turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Shift Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="w-[95vw] max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Square className="h-5 w-5 text-red-500" />
              Terminar Turno
            </DialogTitle>
            <DialogDescription>
              Registe os quilómetros finais e combustível
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Km Summary */}
            {activeShift && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Km Inicial</p>
                <p className="font-semibold">{activeShift.km_start.toLocaleString()} km</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="km_end">Quilómetros Finais *</Label>
              <div className="relative">
                <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="km_end"
                  type="number"
                  value={endForm.km_end}
                  onChange={(e) => setEndForm({...endForm, km_end: e.target.value})}
                  placeholder="Ex: 125150"
                  className="pl-10"
                  data-testid="km-end-input"
                />
              </div>
              {endForm.km_end && activeShift && (
                <p className="text-sm text-emerald-600 font-medium">
                  Total: {(parseFloat(endForm.km_end) - activeShift.km_start).toLocaleString()} km
                </p>
              )}
            </div>

            {/* Fuel Section */}
            <div className="p-4 border rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Fuel className="h-5 w-5 text-amber-500" />
                  <Label>Abasteceu combustível?</Label>
                </div>
                <Switch
                  checked={endForm.fuel_added}
                  onCheckedChange={(checked) => setEndForm({...endForm, fuel_added: checked})}
                  data-testid="fuel-switch"
                />
              </div>

              {endForm.fuel_added && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="fuel_liters">Litros</Label>
                    <Input
                      id="fuel_liters"
                      type="number"
                      step="0.01"
                      value={endForm.fuel_liters}
                      onChange={(e) => setEndForm({...endForm, fuel_liters: e.target.value})}
                      placeholder="Ex: 45.5"
                      data-testid="fuel-liters-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fuel_cost">Custo (€)</Label>
                    <Input
                      id="fuel_cost"
                      type="number"
                      step="0.01"
                      value={endForm.fuel_cost}
                      onChange={(e) => setEndForm({...endForm, fuel_cost: e.target.value})}
                      placeholder="Ex: 72.50"
                      data-testid="fuel-cost-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_notes">Notas (opcional)</Label>
              <Textarea
                id="end_notes"
                value={endForm.notes}
                onChange={(e) => setEndForm({...endForm, notes: e.target.value})}
                placeholder="Observações do turno..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEndModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={endShift}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-end-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Terminar Turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
